import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCopy } from '@fortawesome/free-solid-svg-icons';
import { useLanguage } from '../../contexts/LanguageContext';
import { useSalon } from '../../contexts/SalonContext';
import { getColourHex, getColourLabel } from '../../utils/dressColour';
import AddEditDressModal from './AddEditDressModal';
import PrintDeclarationModal from './PrintDeclarationModal';
import BulkPrintDeclarationsModal from './BulkPrintDeclarationsModal';
import ExportDressesCsvModal from './ExportDressesCsvModal';
import DownloadPriceListModal from './DownloadPriceListModal';
import AddEditReservationModal from './AddEditReservationModal';
import ImageViewerModal from './ImageViewerModal';
import DressDisplay from './DressDisplay';
import TableRowActionsMenu from './TableRowActionsMenu';
import HeaderActionsMenu from './HeaderActionsMenu';
import { getImageUrl } from '../../utils/api';
import useScrollLock from '../../hooks/useScrollLock';
import './Salon.css';

const ManageDresses = ({ salonId, userRole, salon, currentPage: urlPage = 1, onPageChange }) => {
  const { t, formatWeddingDate } = useLanguage();
  const { formatPrice } = useSalon();
  const [dresses, setDresses] = useState([]);
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [currentPage, setCurrentPage] = useState(urlPage);
  const [totalPages, setTotalPages] = useState(1);
  const [sortField, setSortField] = useState('arrival_date');
  const [sortDirection, setSortDirection] = useState('desc');
  const [searchInput, setSearchInput] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const searchInputRef = useRef(null);
  const searchTimeoutRef = useRef(null);
  const previousSearchTermRef = useRef('');
  const [modalOpen, setModalOpen] = useState(false);
  const [editingDress, setEditingDress] = useState(null);
  const [showImageViewer, setShowImageViewer] = useState(false);
  const [viewerImages, setViewerImages] = useState([]);
  const [viewerStartIndex, setViewerStartIndex] = useState(0);

  const [dressReservationsModalOpen, setDressReservationsModalOpen] = useState(false);
  const [dressForReservations, setDressForReservations] = useState(null);
  const [dressReservations, setDressReservations] = useState([]);
  const [dressReservationsLoading, setDressReservationsLoading] = useState(false);
  const [dressReservationsError, setDressReservationsError] = useState('');
  const [reservationEditModalOpen, setReservationEditModalOpen] = useState(false);
  const [selectedReservation, setSelectedReservation] = useState(null);
  const [printDeclarationModalOpen, setPrintDeclarationModalOpen] = useState(false);
  const [dressForDeclaration, setDressForDeclaration] = useState(null);
  const [openActionsMenuDressId, setOpenActionsMenuDressId] = useState(null);
  const [exportCsvModalOpen, setExportCsvModalOpen] = useState(false);
  const [priceListModalOpen, setPriceListModalOpen] = useState(false);
  const [bulkPrintModalOpen, setBulkPrintModalOpen] = useState(false);

  const canDeleteDress = userRole !== 'member';
  const canPrintDeclaration = true;
  const canExportDresses = userRole === 'owner' || userRole === 'admin';

  const headerMenuItems = useMemo(() => {
    const items = [];
    if (salon?.slug) {
      items.push({
        key: 'view-catalogue',
        label: t('dresses.viewPublicCatalogue'),
        onClick: () => window.open(`/shops/${salon.slug}`, '_blank', 'noopener,noreferrer'),
      });
    }
    if (canExportDresses) {
      items.push({
        key: 'export-csv',
        label: t('dresses.exportCsv', 'Export CSV'),
        onClick: () => setExportCsvModalOpen(true),
      });
    }
    items.push({
      key: 'price-list',
      label: t('dresses.downloadPriceList', 'Price List'),
      onClick: () => setPriceListModalOpen(true),
    });
    if (canPrintDeclaration) {
      items.push({
        key: 'print-declarations',
        label: t('dresses.printDeclarations', 'Print declarations'),
        onClick: () => setBulkPrintModalOpen(true),
      });
    }
    return items;
  }, [salon?.slug, canExportDresses, canPrintDeclaration, t]);

  useScrollLock(
    dressReservationsModalOpen
    || reservationEditModalOpen
    || printDeclarationModalOpen
    || exportCsvModalOpen
    || priceListModalOpen
    || bulkPrintModalOpen
  );

  const itemsPerPage = 10;

  // Sync URL page with local state
  useEffect(() => {
    setCurrentPage(urlPage);
  }, [urlPage]);

  const fetchDresses = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`/api/salons/${salonId}/dresses`, {
        headers: {
          'Authorization': `Bearer ${token}`
        },
        params: {
          page: currentPage,
          limit: itemsPerPage,
          sortField: sortField,
          sortDirection: sortDirection,
          search: searchTerm.trim() || undefined
        }
      });
            
      setDresses(response.data.dresses);
      setTotalPages(response.data.pagination.totalPages);
      setError('');
    } catch (error) {
      console.error('Error fetching dresses:', error);
      setError('Failed to fetch dresses');
    } finally {
      setLoading(false);
    }
  }, [salonId, currentPage, sortField, sortDirection, searchTerm]);

  const fetchBrands = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`/api/salons/${salonId}/dress-brands`, {
        headers: {
          'Authorization': `Bearer ${token}`
        },
        params: {
          limit: 100000 // Fetch all brands for dropdown
        }
      });
      setBrands(response.data.brands);
    } catch (error) {
      console.error('Error fetching brands:', error);
    }
  }, [salonId]);

  useEffect(() => {
    fetchDresses();
    fetchBrands();
  }, [fetchDresses, fetchBrands]);

  // Initialize previous search term ref to current search term
  useEffect(() => {
    previousSearchTermRef.current = searchTerm;
  }, [searchTerm]);

  // Debounce search input to avoid too many API calls and maintain focus
  useEffect(() => {
    // Clear previous timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    // Set new timeout to update search term after user stops typing
    searchTimeoutRef.current = setTimeout(() => {
      const trimmedInput = searchInput.trim();
      
      // Only search if at least 2 characters are entered
      if (trimmedInput.length >= 2) {
        // Only update search term and reset page if the value actually changed
        if (trimmedInput !== previousSearchTermRef.current) {
          previousSearchTermRef.current = trimmedInput;
          setSearchTerm(trimmedInput);
          // Reset to first page only when search term actually changes
          if (onPageChange) {
            onPageChange(1);
          } else {
            setCurrentPage(1);
          }
        }
      } else if (trimmedInput.length === 0 && previousSearchTermRef.current !== '') {
        // Clear search if input is empty and we had a previous search
        previousSearchTermRef.current = '';
        setSearchTerm('');
        // Reset to first page when clearing search
        if (onPageChange) {
          onPageChange(1);
        } else {
          setCurrentPage(1);
        }
      }
    }, 300); // 300ms delay

    // Cleanup timeout on unmount
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchInput, onPageChange]);

  const handleAddDress = () => {
    setEditingDress(null);
    setModalOpen(true);
  };

  const handleEditDress = (dress) => {
    setEditingDress(dress);
    setModalOpen(true);
  };

  const handleImageClick = (dress) => {
    if (dress.images && dress.images.length > 0) {
      // Convert relative paths to full URLs
      const fullImageUrls = dress.images.map(imagePath => getImageUrl(imagePath));
      
      // Add video as the last item if it exists
      if (dress.video_url) {
        const videoUrl = getImageUrl(dress.video_url); // This will work for both URLs and file paths
        fullImageUrls.push({
          type: 'video',
          url: videoUrl,
          thumbnail: videoUrl // For now, use the video URL as thumbnail
        });
      }
      
      // Add second video if it exists
      if (dress.video2_url) {
        const video2Url = getImageUrl(dress.video2_url);
        fullImageUrls.push({
          type: 'video',
          url: video2Url,
          thumbnail: video2Url
        });
      }
      
      setViewerImages(fullImageUrls);
      setViewerStartIndex(0);
      setShowImageViewer(true);
    }
  };

  const handleDeleteDress = async (dressId) => {
    if (!window.confirm('Are you sure you want to delete this dress?')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`/api/salons/${salonId}/dresses/${dressId}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      fetchDresses();
    } catch (error) {
      console.error('Error deleting dress:', error);
      const errorMessage = error.response?.data?.error || 'Failed to delete dress';
      alert(errorMessage);
    }
  };

  const handleModalClose = () => {
    setModalOpen(false);
    setEditingDress(null);
  };

  const handleModalSuccess = () => {
    fetchDresses();
    handleModalClose();
  };

  const openPrintDeclarationModal = (dress) => {
    setDressForDeclaration(dress);
    setPrintDeclarationModalOpen(true);
  };

  const getDressOverflowActions = (dress) => {
    const items = [];

    if (salon?.slug) {
      items.push({
        key: 'view-product-page',
        label: t('dresses.viewProductPage', 'View product page'),
        icon: '📄',
        onClick: () => window.open(`/shops/${salon.slug}/dress/${dress.id}`, '_blank', 'noopener,noreferrer'),
      });
    }

    if (canPrintDeclaration) {
      items.push({
        key: 'print-declaration',
        label: t('dresses.printDeclaration', 'Print declaration'),
        icon: '🖨️',
        onClick: () => openPrintDeclarationModal(dress),
      });
    }

    if (canDeleteDress) {
      items.push({
        key: 'delete-dress',
        label: t('dresses.deleteDress'),
        icon: '🗑️',
        danger: true,
        onClick: () => handleDeleteDress(dress.id),
      });
    }

    return items;
  };

  const openDressReservationsModal = async (dress) => {
    setDressForReservations(dress);
    setDressReservationsModalOpen(true);
    setDressReservations([]);
    setDressReservationsLoading(true);
    setDressReservationsError('');
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`/api/salons/${salonId}/reservations`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { dress_id: dress.id },
      });
      setDressReservations(response.data.reservations || []);
    } catch (err) {
      console.error('Error loading dress reservations:', err);
      setDressReservationsError(
        err.response?.data?.error || t('dresses.dressReservationsLoadError')
      );
    } finally {
      setDressReservationsLoading(false);
    }
  };

  const closeDressReservationsModal = () => {
    setDressReservationsModalOpen(false);
    setDressForReservations(null);
    setDressReservations([]);
    setDressReservationsError('');
    setReservationEditModalOpen(false);
    setSelectedReservation(null);
  };

  const handleDressReservationRowClick = (reservation) => {
    setSelectedReservation(reservation);
    setReservationEditModalOpen(true);
  };

  const handleReservationModalSuccess = async () => {
    fetchDresses();
    setReservationEditModalOpen(false);
    setSelectedReservation(null);
    const dressId = dressForReservations?.id;
    if (dressId) {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`/api/salons/${salonId}/reservations`, {
          headers: { Authorization: `Bearer ${token}` },
          params: { dress_id: dressId },
        });
        setDressReservations(response.data.reservations || []);
      } catch (err) {
        console.error('Error refreshing dress reservations:', err);
      }
    }
  };

  const handlePageChange = (newPage) => {
    if (onPageChange) {
      onPageChange(newPage);
    } else {
      setCurrentPage(newPage);
    }
  };

  const handleSort = (field) => {
    if (sortField === field) {
      // Toggle direction if same field
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // New field, set default direction based on field type
      setSortField(field);
      
      // Numerical and date fields default to descending, text fields to ascending
      const descendingFields = ['rental_price', 'selling_price', 'arrival_date', 'reservation_count'];
      const defaultDirection = descendingFields.includes(field) ? 'desc' : 'asc';
      
      setSortDirection(defaultDirection);
    }
    if (onPageChange) {
      onPageChange(1); // Reset to first page when sorting
    } else {
      setCurrentPage(1);
    }
  };

  const handleSearchChange = (e) => {
    setSearchInput(e.target.value);
    // Focus is maintained automatically since we're not causing immediate re-render
  };

  const renderPagination = () => {
    if (totalPages <= 1) return null;

    const pages = [];
    for (let i = 1; i <= totalPages; i++) {
      pages.push(
        <button
          key={i}
          className={`pagination-btn ${currentPage === i ? 'active' : ''}`}
          onClick={() => handlePageChange(i)}
        >
          {i}
        </button>
      );
    }

    return (
      <div className="pagination">
        <button
          className="pagination-btn"
          onClick={() => handlePageChange(currentPage - 1)}
          disabled={currentPage === 1}
        >
          {t('common.previous')}
        </button>
        {pages}
        <button
          className="pagination-btn"
          onClick={() => handlePageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
        >
          {t('common.next')}
        </button>
      </div>
    );
  };

  const formatColour = (colour) => {
    if (!colour) return '-';
    const translatedColor = getColourLabel(t, colour);
    return (
      <div className="colour-display">
        <div
          className="colour-circle"
          style={{ backgroundColor: getColourHex(colour) }}
          title={translatedColor}
        ></div>
        <span className="colour-name">
          {translatedColor}
        </span>
      </div>
    );
  };

  const formatYear = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).getFullYear();
  };

  if (loading) {
    return (
      <div className="manage-page">
        <div className="loading-state">
          <div className="loading-spinner"></div>
          <p>{t('messages.loading.dresses')}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="manage-page">
        <div className="error-state">
          <div className="error-icon">⚠️</div>
          <h3>{t('common.error')}</h3>
          <p>{error}</p>
          <button className="salon-button secondary" onClick={fetchDresses}>
            {t('common.search')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="manage-page">
      <div className="table-header">
        <h3>{t('dresses.title')}</h3>
        <div className="header-actions">
          <div className="search-container">
            <input
              ref={searchInputRef}
              type="text"
              className="search-input"
              placeholder={t('dresses.searchDress')}
              value={searchInput}
              onChange={handleSearchChange}
            />
          </div>
          <div className="header-actions-primary-row">
            {canExportDresses && (
              <button className="salon-button primary" onClick={handleAddDress}>
                {t('dresses.addDress')}
              </button>
            )}
            {headerMenuItems.length > 0 && (
              <HeaderActionsMenu
                items={headerMenuItems}
                label={t('common.actions')}
                ariaLabel={t('common.actions')}
              />
            )}
          </div>
        </div>
      </div>

      {dresses.length === 0 ? (
        <div className="table-empty-state">
          <div className="table-empty-state-icon">👗</div>
          <h4>{t('dresses.noDresses')}</h4>
          <p>{t('dresses.startCollection', 'Start building your dress collection by adding your first dress.')}</p>
          {(userRole === 'owner' || userRole === 'admin') && (
            <button className="salon-button primary" onClick={handleAddDress}>
              {t('dresses.addDress')}
            </button>
          )}
        </div>
      ) : (
        <div className="table-container">
          <div className="table-wrapper">
            <table className="data-table">
              <thead>
                <tr>
                  <th 
                    className="sortable fixed-column" 
                    onClick={() => handleSort('name')}
                    title={`${t('common.tooltips.clickToSort')} ${t('dresses.dressName').toLowerCase()}`}
                  >
                    {t('dresses.dressName')}
                    {sortField === 'name' && (
                      <span className="sort-indicator">
                        {sortDirection === 'asc' ? '↑' : '↓'}
                      </span>
                    )}
                  </th>
                <th 
                  className="sortable" 
                  onClick={() => handleSort('size')}
                  title={`${t('common.tooltips.clickToSort')} ${t('dresses.size').toLowerCase()}`}
                >
                  {t('dresses.size')}
                  {sortField === 'size' && (
                    <span className="sort-indicator">
                      {sortDirection === 'asc' ? '↑' : '↓'}
                    </span>
                  )}
                </th>
                <th 
                  className="sortable" 
                  onClick={() => handleSort('colour')}
                  title={`${t('common.tooltips.clickToSort')} ${t('dresses.color').toLowerCase()}`}
                >
                  {t('dresses.color')}
                  {sortField === 'colour' && (
                    <span className="sort-indicator">
                      {sortDirection === 'asc' ? '↑' : '↓'}
                    </span>
                  )}
                </th>
                <th 
                  className="sortable" 
                  onClick={() => handleSort('rental_price')}
                  title={`${t('common.tooltips.clickToSort')} ${t('dresses.rentalPrice').toLowerCase()}`}
                >
                  {t('dresses.rentalPrice')}
                  {sortField === 'rental_price' && (
                    <span className="sort-indicator">
                      {sortDirection === 'asc' ? '↑' : '↓'}
                    </span>
                  )}
                </th>
                <th 
                  className="sortable" 
                  onClick={() => handleSort('selling_price')}
                  title={`${t('common.tooltips.clickToSort')} ${t('dresses.sellingPrice').toLowerCase()}`}
                >
                  {t('dresses.sellingPrice')}
                  {sortField === 'selling_price' && (
                    <span className="sort-indicator">
                      {sortDirection === 'asc' ? '↑' : '↓'}
                    </span>
                  )}
                </th>
                <th 
                  className="sortable" 
                  onClick={() => handleSort('arrival_date')}
                  title={`${t('common.tooltips.clickToSort')} ${t('dresses.arrivalYear').toLowerCase()}`}
                >
                  {t('dresses.arrivalYear')}
                  {sortField === 'arrival_date' && (
                    <span className="sort-indicator">
                      {sortDirection === 'asc' ? '↑' : '↓'}
                    </span>
                  )}
                </th>
                <th 
                  className="sortable" 
                  onClick={() => handleSort('stock_count')}
                  title={`${t('common.tooltips.clickToSort')} ${t('dresses.stockCount').toLowerCase()}`}
                >
                  {t('dresses.stockCount')}
                  {sortField === 'stock_count' && (
                    <span className="sort-indicator">
                      {sortDirection === 'asc' ? '↑' : '↓'}
                    </span>
                  )}
                </th>
                <th 
                  className="sortable" 
                  onClick={() => handleSort('reservation_count')}
                  title={`${t('common.tooltips.clickToSort')} ${t('dresses.reserveCount').toLowerCase()}`}
                >
                  {t('dresses.reserveCount')}
                  {sortField === 'reservation_count' && (
                    <span className="sort-indicator">
                      {sortDirection === 'asc' ? '↑' : '↓'}
                    </span>
                  )}
                </th>
                <th>{t('common.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {dresses.map((dress) => {
                const overflowActions = getDressOverflowActions(dress);
                return (
                <tr key={dress.id}>
                  <td className="fixed-column">
                    <div className="table-cell-content">
                      <DressDisplay 
                        dress={dress}
                        onImageClick={handleImageClick}
                        imageClassName="table-preview-image"
                      />
                    </div>
                  </td>
                  <td>
                    <div className="table-cell-content">
                      {dress.size || '-'}
                    </div>
                  </td>
                  <td>
                    <div className="table-cell-content">
                      {formatColour(dress.colour)}
                    </div>
                  </td>
                  <td>
                    <div className="table-cell-content">
                      <span className="price-value rental">{formatPrice(dress.rental_price)}</span>
                    </div>
                  </td>
                  <td>
                    <div className="table-cell-content">
                      <span className="price-value sale">{formatPrice(dress.selling_price)}</span>
                    </div>
                  </td>
                  <td>
                    <div className="table-cell-content">
                      {formatYear(dress.arrival_date)}
                    </div>
                  </td>
                  <td>
                    <div className="table-cell-content">
                      {dress.stock_count === 0 ? (
                        <span className="stock-count zero">{dress.stock_count}</span>
                      ) : (
                        <span>{dress.stock_count || 1}</span>
                      )}
                    </div>
                  </td>
                  <td>
                    <div className="table-cell-content">
                      <span className="reservation-count">{dress.reservation_count || 0}</span>
                    </div>
                  </td>
                  <td>
                    <div className="table-cell-content">
                      <div className="table-actions">
                        <button 
                          className="action-button edit"
                          onClick={() => handleEditDress(dress)}
                          title={t('dresses.editDress')}
                        >
                          ✏️
                        </button>
                        <button
                          type="button"
                          className="action-button"
                          onClick={() => openDressReservationsModal(dress)}
                          title={t('dresses.viewDressReservations')}
                        >
                          📋
                        </button>
                        {overflowActions.length > 0 && (
                          <TableRowActionsMenu
                            items={overflowActions}
                            isOpen={openActionsMenuDressId === dress.id}
                            onToggle={() => setOpenActionsMenuDressId(
                              openActionsMenuDressId === dress.id ? null : dress.id
                            )}
                            onClose={() => setOpenActionsMenuDressId(null)}
                            ariaLabel={t('common.moreActions', 'More actions')}
                          />
                        )}
                      </div>
                    </div>
                  </td>
                </tr>
              );
              })}
            </tbody>
          </table>
          </div>
        </div>
      )}

      {/* Pagination */}
      <div className="pagination-container">
        {renderPagination()}
      </div>

      <AddEditDressModal
        isOpen={modalOpen}
        onClose={handleModalClose}
        onSuccess={handleModalSuccess}
        dress={editingDress}
        salonId={salonId}
        brands={brands}
        userRole={userRole}
      />

      <ImageViewerModal
        isOpen={showImageViewer}
        onClose={() => setShowImageViewer(false)}
        images={viewerImages}
        currentIndex={viewerStartIndex}
      />

      <PrintDeclarationModal
        isOpen={printDeclarationModalOpen}
        onClose={() => {
          setPrintDeclarationModalOpen(false);
          setDressForDeclaration(null);
        }}
        salonId={salonId}
        salon={salon}
        dress={dressForDeclaration}
      />

      <BulkPrintDeclarationsModal
        isOpen={bulkPrintModalOpen}
        onClose={() => setBulkPrintModalOpen(false)}
        salonId={salonId}
        salon={salon}
      />

      <ExportDressesCsvModal
        isOpen={exportCsvModalOpen}
        onClose={() => setExportCsvModalOpen(false)}
        salonId={salonId}
      />

      <DownloadPriceListModal
        isOpen={priceListModalOpen}
        onClose={() => setPriceListModalOpen(false)}
        salonId={salonId}
        salonName={salon?.name}
        canIncludeZeroStock={canExportDresses}
      />

      {dressReservationsModalOpen && dressForReservations && (
        <div className="modal-overlay" onClick={closeDressReservationsModal}>
          <div
            className="modal dress-reservations-modal"
            style={{ maxWidth: '640px' }}
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-labelledby="dress-reservations-modal-title"
          >
            <div className="modal-header">
              <h3 id="dress-reservations-modal-title">
                {t('dresses.dressReservationsModalTitle', 'Reservations', {
                  dressName: dressForReservations.name || '',
                })}
              </h3>
              <button type="button" className="modal-close" onClick={closeDressReservationsModal}>
                ×
              </button>
            </div>
            <div className="modal-body">
              {dressReservationsError && (
                <div className="error-message" style={{ marginBottom: '16px' }}>
                  {dressReservationsError}
                </div>
              )}
              {dressReservationsLoading && (
                <div style={{ textAlign: 'center', padding: '40px' }}>
                  <div className="loading-spinner" />
                  <p>{t('messages.loading.reservations', 'Loading reservations...')}</p>
                </div>
              )}
              {!dressReservationsLoading && !dressReservationsError && dressReservations.length === 0 && (
                <div style={{ textAlign: 'center', padding: '40px', color: '#718096' }}>
                  <p>{t('dresses.noReservationsForDress')}</p>
                </div>
              )}
              {!dressReservationsLoading && dressReservations.length > 0 && (
                <div className="dress-reservations-list-scroll">
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    {dressReservations.map((reservation) => (
                      <div
                        key={reservation.id}
                        role="button"
                        tabIndex={0}
                        onClick={() => handleDressReservationRowClick(reservation)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            handleDressReservationRowClick(reservation);
                          }
                        }}
                        style={{
                          padding: '16px',
                          border: '1px solid #e2e8f0',
                          borderRadius: '12px',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                          backgroundColor: '#fff',
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = '#f7fafc';
                          e.currentTarget.style.borderColor = '#667eea';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = '#fff';
                          e.currentTarget.style.borderColor = '#e2e8f0';
                        }}
                      >
                        <div
                          style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'flex-start',
                            marginBottom: '8px',
                          }}
                        >
                          <div>
                            <div
                              style={{
                                fontWeight: '600',
                                fontSize: '1.1rem',
                                color: '#2d3748',
                                marginBottom: '4px',
                              }}
                            >
                              {reservation.bride_name} {reservation.bride_surname}
                            </div>
                            {reservation.wedding_date && (
                              <div style={{ color: '#718096', fontSize: '0.95rem' }}>
                                {t('reservations.weddingDate')}:{' '}
                                {formatWeddingDate(reservation.wedding_date)}
                              </div>
                            )}
                          </div>
                          <div
                            style={{
                              padding: '4px 12px',
                              borderRadius: '8px',
                              fontSize: '0.85rem',
                              fontWeight: '600',
                              backgroundColor:
                                reservation.transaction_type === 'rental' ? '#e6fffa' : '#fff5f5',
                              color:
                                reservation.transaction_type === 'rental' ? '#047857' : '#c53030',
                            }}
                          >
                            {reservation.transaction_type === 'rental'
                              ? t('reservations.rental')
                              : t('reservations.sale')}
                          </div>
                        </div>
                        {reservation.bride_telephone && (
                          <div
                            className="dress-reservation-phone-row"
                            style={{ color: '#4a5568', fontSize: '0.9rem', marginTop: '8px' }}
                          >
                            <span>📞 {reservation.bride_telephone}</span>
                            <button
                              type="button"
                              className="dress-reservation-copy-phone"
                              onClick={(e) => {
                                e.stopPropagation();
                                navigator.clipboard
                                  .writeText(String(reservation.bride_telephone).trim())
                                  .catch(() => {});
                              }}
                              title={t('common.copy')}
                              aria-label={t('common.copy')}
                            >
                              <FontAwesomeIcon icon={faCopy} />
                            </button>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <AddEditReservationModal
        isOpen={reservationEditModalOpen}
        onClose={() => {
          setReservationEditModalOpen(false);
          setSelectedReservation(null);
        }}
        salonId={salonId}
        onReservationAdded={handleReservationModalSuccess}
        reservation={selectedReservation}
        mode="edit"
        userRole={userRole}
      />
    </div>
  );
};

export default ManageDresses;

